import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export type NavDensity = 'comfortable' | 'compact'

/** Legacy key from when density was a user preference; clear so reloads don't flash expanded. */
const LEGACY_STORAGE_KEY = 'nav-density:v1'

try {
  localStorage.removeItem(LEGACY_STORAGE_KEY)
} catch {
  // incognito / quota
}

interface NavDensityContextValue {
  density: NavDensity
  setDensity: (value: NavDensity) => void
}

const NavDensityContext = createContext<NavDensityContextValue | undefined>(undefined)

export const NavDensityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Session-only. The rail stays compact until the open button or a section click.
  // Never persist across loads.
  const [density, setDensityState] = useState<NavDensity>('compact')

  const setDensity = useCallback((value: NavDensity) => {
    setDensityState(value)
  }, [])

  const value = useMemo(() => ({ density, setDensity }), [density, setDensity])

  return <NavDensityContext.Provider value={value}>{children}</NavDensityContext.Provider>
}

export const useNavDensity = (): NavDensityContextValue => {
  const ctx = useContext(NavDensityContext)
  if (!ctx) {
    throw new Error('useNavDensity must be used within NavDensityProvider')
  }
  return ctx
}
